import { useState, useEffect, useCallback } from "react";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { toast } from "sonner";
import { Link, Navigate } from "react-router-dom";
import Logo from "../components/Logo";
import {
  Users,
  Search,
  RefreshCw,
  ChevronLeft,
  Loader2,
  Eye,
  MoreHorizontal,
  Play,
  Mail,
  Crown,
  ShieldX,
  Unplug,
  Copy,
  ExternalLink,
  Store,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  DollarSign,
  Calendar
} from "lucide-react";

const ADMIN_EMAILS = ["reynir@replydriven.com"];

const AdminCustomers = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  // Drawer state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  
  // Modal states
  const [grantModal, setGrantModal] = useState({ open: false, customer: null });
  const [grantReason, setGrantReason] = useState("");
  const [revokeModal, setRevokeModal] = useState({ open: false, customer: null });
  const [runAnalysisModal, setRunAnalysisModal] = useState({ open: false, customer: null });
  const [resendReportModal, setResendReportModal] = useState({ open: false, customer: null });
  const [disconnectStoreModal, setDisconnectStoreModal] = useState({ open: false, customer: null, store: null });
  const [actionLoading, setActionLoading] = useState(false);

  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("q", searchQuery);
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (planFilter !== "all") params.append("plan", planFilter);
      params.append("limit", "50");
      
      const res = await api.get(`/admin/customers?${params.toString()}`);
      setCustomers(res.data?.customers || []);
      setTotalCount(res.data?.total || 0);
    } catch (err) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, planFilter]);

  useEffect(() => {
    if (isAdmin) {
      fetchCustomers();
    }
  }, [isAdmin, fetchCustomers]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers();
  };

  const openCustomerDrawer = async (customerId) => {
    setDrawerOpen(true);
    setLoadingCustomer(true);
    try {
      const res = await api.get(`/admin/customers/${customerId}`);
      setSelectedCustomer(res.data);
    } catch (err) {
      toast.error("Failed to load customer details");
      setDrawerOpen(false);
    } finally {
      setLoadingCustomer(false);
    }
  };

  const handleGrantLifetime = async () => {
    if (!grantReason.trim()) {
      toast.error("Reason is required");
      return;
    }
    setActionLoading(true);
    try {
      await api.post("/admin/users/grant-lifetime", {
        email: grantModal.customer.email,
        reason: grantReason.trim()
      });
      toast.success("Lifetime access granted.");
      setGrantModal({ open: false, customer: null });
      setGrantReason("");
      fetchCustomers();
      if (selectedCustomer?.user_id === grantModal.customer.user_id) {
        openCustomerDrawer(selectedCustomer.user_id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to grant access");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeLifetime = async () => {
    setActionLoading(true);
    try {
      await api.post("/admin/users/revoke-lifetime", {
        email: revokeModal.customer.email
      });
      toast.success("Lifetime access revoked.");
      setRevokeModal({ open: false, customer: null });
      fetchCustomers();
      if (selectedCustomer?.user_id === revokeModal.customer.user_id) {
        openCustomerDrawer(selectedCustomer.user_id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revoke access");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRunAnalysis = async () => {
    setActionLoading(true);
    try {
      await api.post(`/admin/customers/${runAnalysisModal.customer.user_id}/run-analysis`);
      toast.success("Analysis started.");
      setRunAnalysisModal({ open: false, customer: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start analysis");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendReport = async () => {
    setActionLoading(true);
    try {
      await api.post(`/admin/customers/${resendReportModal.customer.user_id}/resend-report`);
      toast.success("Report queued to send.");
      setResendReportModal({ open: false, customer: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend report");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisconnectStore = async () => {
    setActionLoading(true);
    try {
      await api.post(`/admin/customers/${disconnectStoreModal.customer.user_id}/stores/${disconnectStoreModal.store.store_id}/disconnect`);
      toast.success("Store disconnected.");
      setDisconnectStoreModal({ open: false, customer: null, store: null });
      if (selectedCustomer?.user_id === disconnectStoreModal.customer.user_id) {
        openCustomerDrawer(selectedCustomer.user_id);
      }
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to disconnect store");
    } finally {
      setActionLoading(false);
    }
  };

  const copyJson = () => {
    if (selectedCustomer?.raw) {
      navigator.clipboard.writeText(JSON.stringify(selectedCustomer.raw, null, 2));
      toast.success("JSON copied to clipboard");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  const getBillingBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Active</Badge>;
      case "trialing":
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Trialing</Badge>;
      case "lifetime":
        return <Badge className="bg-[#D4F34A]/20 text-[#D4F34A] border-[#D4F34A]/30">Lifetime</Badge>;
      case "cancelled":
      case "canceled":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Canceled</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">None</Badge>;
    }
  };

  if (!user) return <Navigate to="/" />;
  if (!isAdmin) return <Navigate to="/dashboard" />;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <Logo showText linkTo="/" />
            <span className="text-text-muted hidden sm:inline">/</span>
            <Link to="/dashboard" className="text-text-muted hover:text-white text-sm hidden sm:inline">Dashboard</Link>
            <span className="text-text-muted hidden sm:inline">/</span>
            <span className="text-white text-sm font-medium">Admin</span>
          </div>
          <Link to="/dashboard" className="flex-shrink-0">
            <Button variant="ghost" size="sm" className="text-text-muted hover:text-white px-2 sm:px-4">
              <ChevronLeft className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Back</span>
            </Button>
          </Link>
        </div>
      </nav>

      {/* Admin Sub-Navigation */}
      <div className="fixed top-[57px] sm:top-[65px] left-0 right-0 z-40 bg-[#0a0a0a] border-b border-[#262626] overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-1 min-w-max">
            <Link to="/admin" className="px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white whitespace-nowrap">Overview</Link>
            <Link to="/admin/affiliates" className="px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white whitespace-nowrap">Affiliates</Link>
            <Link to="/admin/users" className="px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white whitespace-nowrap">Users</Link>
            <Link to="/admin/customers" className="px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 border-[#D4F34A] text-white whitespace-nowrap">Customers</Link>
            <Link to="/admin/payouts" className="px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white whitespace-nowrap">Payouts</Link>
          </div>
        </div>
      </div>

      <main className="pt-28 sm:pt-32 pb-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
              <Users className="w-8 h-8 text-[#D4F34A]" />
              Customers
            </h1>
            <p className="text-text-muted text-sm mt-1">
              Search and manage subscriptions, stores, and reports.
            </p>
          </div>

          {/* Search + Filters */}
          <Card className="bg-surface border-[#262626] mb-6">
            <CardContent className="py-4">
              <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                  <Input
                    placeholder="Search by email, user id, or store domain…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-[#1a1a1a] border-[#262626] text-white"
                    data-testid="customer-search-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-32 bg-[#1a1a1a] border-[#262626]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-[#262626]">
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="trialing">Trialing</SelectItem>
                      <SelectItem value="cancelled">Canceled</SelectItem>
                      <SelectItem value="lifetime">Lifetime</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={planFilter} onValueChange={setPlanFilter}>
                    <SelectTrigger className="w-32 bg-[#1a1a1a] border-[#262626]">
                      <SelectValue placeholder="Plan" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-[#262626]">
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="operator">Operator</SelectItem>
                      <SelectItem value="agency">Agency</SelectItem>
                      <SelectItem value="lifetime">Lifetime</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={fetchCustomers}
                    className="border-[#262626] text-text-muted hover:text-white"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Results Table */}
          <Card className="bg-surface border-[#262626]">
            <CardContent className="p-0">
              {loading ? (
                <div className="py-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-text-muted" />
                </div>
              ) : customers.length === 0 ? (
                <div className="py-12 text-center text-text-muted">No customers found.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#262626] bg-[#0a0a0a]">
                        <th className="text-left py-3 px-4 text-xs font-medium text-text-muted uppercase">Customer</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-text-muted uppercase">Billing</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-text-muted uppercase">MRR</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-text-muted uppercase">Stores</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-text-muted uppercase">Last Analysis</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-text-muted uppercase">Last Report</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-text-muted uppercase">Issues</th>
                        <th className="text-right py-3 px-4 text-xs font-medium text-text-muted uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#262626]">
                      {customers.map((customer) => (
                        <tr
                          key={customer.user_id}
                          className="hover:bg-white/5 cursor-pointer"
                          onClick={() => openCustomerDrawer(customer.user_id)}
                        >
                          <td className="py-3 px-4">
                            <p className="text-white font-medium">{customer.email}</p>
                            <p className="text-xs text-text-muted">
                              {customer.user_id.slice(0, 12)}… • Created {formatDate(customer.created_at)}
                            </p>
                          </td>
                          <td className="py-3 px-4">
                            {getBillingBadge(customer.billing_status)}
                            <p className="text-xs text-text-muted mt-1">Plan: {customer.plan}</p>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-white font-medium">${customer.mrr || 0}</p>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-white">{customer.connected_stores_count}/{customer.max_stores}</p>
                            {customer.stores?.length > 0 && (
                              <p className="text-xs text-text-muted truncate max-w-[120px]">
                                {customer.stores[0]?.domain}
                                {customer.stores.length > 1 && ` +${customer.stores.length - 1}`}
                              </p>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-text-muted">
                            {formatRelativeTime(customer.last_analysis_at)}
                          </td>
                          <td className="py-3 px-4 text-sm text-text-muted">
                            {customer.last_report_at ? `Sent ${formatRelativeTime(customer.last_report_at)}` : "Never"}
                          </td>
                          <td className="py-3 px-4">
                            {customer.issues?.sync_failing || customer.issues?.email_disabled ? (
                              <div className="flex flex-wrap gap-1">
                                {customer.issues.sync_failing && (
                                  <Badge variant="outline" className="text-xs border-red-500/30 text-red-400">Sync failing</Badge>
                                )}
                                {customer.issues.email_disabled && (
                                  <Badge variant="outline" className="text-xs border-yellow-500/30 text-yellow-400">Email disabled</Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-text-muted">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openCustomerDrawer(customer.user_id)}
                                className="text-[#D4F34A] hover:bg-[#D4F34A]/10"
                              >
                                <Eye className="w-4 h-4 mr-1" /> View
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm" variant="ghost" className="text-text-muted">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-[#1a1a1a] border-[#262626]">
                                  <DropdownMenuItem onClick={() => setRunAnalysisModal({ open: true, customer })}>
                                    <Play className="w-4 h-4 mr-2" /> Run analysis now
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => setResendReportModal({ open: true, customer })}>
                                    <Mail className="w-4 h-4 mr-2" /> Resend last report
                                  </DropdownMenuItem>
                                  {!customer.lifetime_access ? (
                                    <DropdownMenuItem onClick={() => setGrantModal({ open: true, customer })}>
                                      <Crown className="w-4 h-4 mr-2" /> Grant lifetime access…
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem onClick={() => setRevokeModal({ open: true, customer })} className="text-red-400">
                                      <ShieldX className="w-4 h-4 mr-2" /> Revoke lifetime access…
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!loading && customers.length > 0 && (
                <div className="px-4 py-3 border-t border-[#262626] text-sm text-text-muted">
                  Showing {customers.length} of {totalCount} customers
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Customer Detail Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="bg-[#0a0a0a] border-[#262626] w-full sm:max-w-lg overflow-y-auto">
          {loadingCustomer ? (
            <div className="py-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-text-muted" />
            </div>
          ) : selectedCustomer ? (
            <>
              <SheetHeader>
                <SheetTitle className="text-white">{selectedCustomer.email}</SheetTitle>
                <SheetDescription className="text-text-muted">
                  {selectedCustomer.user_id} • Created {formatDate(selectedCustomer.created_at)}
                </SheetDescription>
                <Button size="sm" variant="outline" onClick={copyJson} className="mt-2 w-fit border-[#262626]">
                  <Copy className="w-4 h-4 mr-2" /> Copy JSON
                </Button>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Billing Section */}
                <Card className="bg-surface border-[#262626]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-[#D4F34A]" /> Billing
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-text-muted text-sm">Status</span>
                      {getBillingBadge(selectedCustomer.billing?.status)}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-text-muted text-sm">Plan</span>
                      <span className="text-white">{selectedCustomer.billing?.plan}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-text-muted text-sm">MRR</span>
                      <span className="text-white">${selectedCustomer.billing?.mrr || 0}</span>
                    </div>
                    {selectedCustomer.billing?.next_billing_at && (
                      <div className="flex items-center justify-between">
                        <span className="text-text-muted text-sm">Next billing</span>
                        <span className="text-white">{formatDate(selectedCustomer.billing.next_billing_at)}</span>
                      </div>
                    )}
                    <div className="pt-2 flex gap-2">
                      {!selectedCustomer.billing?.lifetime_access ? (
                        <Button
                          size="sm"
                          onClick={() => setGrantModal({ open: true, customer: selectedCustomer })}
                          className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90"
                        >
                          <Crown className="w-4 h-4 mr-1" /> Grant lifetime
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setRevokeModal({ open: true, customer: selectedCustomer })}
                        >
                          <ShieldX className="w-4 h-4 mr-1" /> Revoke lifetime
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Stores Section */}
                <Card className="bg-surface border-[#262626]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Store className="w-4 h-4 text-[#D4F34A]" /> Stores ({selectedCustomer.connected_stores_count}/{selectedCustomer.billing?.max_stores || 10})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedCustomer.stores?.length > 0 ? (
                      <div className="space-y-2">
                        {selectedCustomer.stores.map((store) => (
                          <div key={store.store_id} className="flex items-center justify-between py-2 border-b border-[#262626] last:border-0">
                            <div className="flex items-center gap-2">
                              {store.status === "ok" ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <XCircle className="w-4 h-4 text-red-400" />
                              )}
                              <span className="text-white text-sm">{store.domain}</span>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-400 hover:bg-red-500/10"
                              onClick={() => setDisconnectStoreModal({ open: true, customer: selectedCustomer, store })}
                            >
                              <Unplug className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-text-muted text-sm">No stores connected.</p>
                    )}
                  </CardContent>
                </Card>

                {/* Reports Section */}
                <Card className="bg-surface border-[#262626]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[#D4F34A]" /> Reports / Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-text-muted text-sm">Last analysis</span>
                      <span className="text-white">{formatRelativeTime(selectedCustomer.last_analysis_at)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-text-muted text-sm">Last report sent</span>
                      <span className="text-white">{formatRelativeTime(selectedCustomer.last_report_at)}</span>
                    </div>
                    <div className="pt-2 flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#262626]"
                        onClick={() => setRunAnalysisModal({ open: true, customer: selectedCustomer })}
                      >
                        <Play className="w-4 h-4 mr-1" /> Run analysis now
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#262626]"
                        onClick={() => setResendReportModal({ open: true, customer: selectedCustomer })}
                      >
                        <Mail className="w-4 h-4 mr-1" /> Resend last report
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Activity Section */}
                <Card className="bg-surface border-[#262626]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#D4F34A]" /> Activity
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedCustomer.activity?.length > 0 ? (
                      <div className="space-y-2">
                        {selectedCustomer.activity.map((event, idx) => (
                          <div key={idx} className="flex items-start gap-3 py-2 border-b border-[#262626] last:border-0">
                            <div className="w-2 h-2 rounded-full bg-[#D4F34A] mt-1.5" />
                            <div>
                              <p className="text-white text-sm">{event.summary}</p>
                              <p className="text-xs text-text-muted">{formatRelativeTime(event.at)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-text-muted text-sm">No recent activity.</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Grant Lifetime Modal */}
      <Dialog open={grantModal.open} onOpenChange={(open) => !actionLoading && setGrantModal({ open, customer: open ? grantModal.customer : null })}>
        <DialogContent className="bg-[#1a1a1a] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle>Grant lifetime access</DialogTitle>
            <DialogDescription className="text-text-muted">
              This user will bypass billing and keep access even without a subscription.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder='e.g. "Tester / Partner / Founder comp"'
              value={grantReason}
              onChange={(e) => setGrantReason(e.target.value)}
              className="bg-[#0a0a0a] border-[#262626] text-white"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGrantModal({ open: false, customer: null })} disabled={actionLoading}>Cancel</Button>
            <Button onClick={handleGrantLifetime} disabled={actionLoading || !grantReason.trim()} className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Grant lifetime
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Lifetime Modal */}
      <Dialog open={revokeModal.open} onOpenChange={(open) => !actionLoading && setRevokeModal({ open, customer: open ? revokeModal.customer : null })}>
        <DialogContent className="bg-[#1a1a1a] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle>Revoke lifetime access</DialogTitle>
            <DialogDescription className="text-text-muted">
              This user will need an active subscription to use paid features.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRevokeModal({ open: false, customer: null })} disabled={actionLoading}>Cancel</Button>
            <Button variant="destructive" onClick={handleRevokeLifetime} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Run Analysis Modal */}
      <Dialog open={runAnalysisModal.open} onOpenChange={(open) => !actionLoading && setRunAnalysisModal({ open, customer: open ? runAnalysisModal.customer : null })}>
        <DialogContent className="bg-[#1a1a1a] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle>Run analysis now</DialogTitle>
            <DialogDescription className="text-text-muted">
              This will run analysis for all connected stores for this customer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRunAnalysisModal({ open: false, customer: null })} disabled={actionLoading}>Cancel</Button>
            <Button onClick={handleRunAnalysis} disabled={actionLoading} className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Run
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend Report Modal */}
      <Dialog open={resendReportModal.open} onOpenChange={(open) => !actionLoading && setResendReportModal({ open, customer: open ? resendReportModal.customer : null })}>
        <DialogContent className="bg-[#1a1a1a] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle>Resend last report</DialogTitle>
            <DialogDescription className="text-text-muted">
              Resends the most recent report email to {resendReportModal.customer?.email}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResendReportModal({ open: false, customer: null })} disabled={actionLoading}>Cancel</Button>
            <Button onClick={handleResendReport} disabled={actionLoading} className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Resend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disconnect Store Modal */}
      <Dialog open={disconnectStoreModal.open} onOpenChange={(open) => !actionLoading && setDisconnectStoreModal({ open, customer: open ? disconnectStoreModal.customer : null, store: open ? disconnectStoreModal.store : null })}>
        <DialogContent className="bg-[#1a1a1a] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle>Disconnect store</DialogTitle>
            <DialogDescription className="text-text-muted">
              Disconnect {disconnectStoreModal.store?.domain}? This stops data sync and weekly reports for that store.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDisconnectStoreModal({ open: false, customer: null, store: null })} disabled={actionLoading}>Cancel</Button>
            <Button variant="destructive" onClick={handleDisconnectStore} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCustomers;
